import { Web3EvmInteraction } from '@/abi/Web3Evm';
import { Web3SolanaProgramInteraction } from '@/program/web3';
import { Web3DefaiProgramInteraction } from '@/program/web3DefaiLaunchpad';
import { Web3MemeLaunchPadProgramInteraction } from '@/program/web3MemeLaunchpad';
import { CoinType } from '@/zustand-store/coin/useCoinStore';
import usePersistStore from '@/zustand-store/persist-config/usePersistStore';

type NetworkType = 'solana' | 'evm';

type Web3Instance = 
  | Web3MemeLaunchPadProgramInteraction
  | Web3DefaiProgramInteraction
  | Web3SolanaProgramInteraction
  | Web3EvmInteraction;

interface Web3Instances {
  evm: Web3EvmInteraction;
  meme: Web3MemeLaunchPadProgramInteraction;
  defai: Web3DefaiProgramInteraction;
  agent: Web3SolanaProgramInteraction;
}

// Singleton instances
const instances: Web3Instances = {
  evm: new Web3EvmInteraction(),
  meme: new Web3MemeLaunchPadProgramInteraction(),
  defai: new Web3DefaiProgramInteraction(),
  agent: new Web3SolanaProgramInteraction()
};

const COIN_TYPE_TO_INSTANCE_MAP: Record<CoinType, keyof Pick<Web3Instances, 'agent' | 'defai' | 'meme'>> = {
  [CoinType.AGENT]: 'agent',
  [CoinType.DEFAI]: 'defai',
  [CoinType.MEME]: 'meme'
};

function getSolanaInstance(type: CoinType): Web3Instance {
  const instanceKey = COIN_TYPE_TO_INSTANCE_MAP[type] ?? 'agent';
  return instances[instanceKey];
}

function getEvmInstance(): Web3EvmInteraction {
  return instances.evm;
}

const NETWORK_INSTANCE_MAP: Record<NetworkType, (type: CoinType) => Web3Instance> = {
  solana: getSolanaInstance,
  evm: getEvmInstance
} as const;

export class Web3TradingService {
  private static instance: Web3TradingService | null = null;

  private constructor() {}

  static getInstance(type: CoinType): Web3Instance {
    const currentNetwork = usePersistStore.getState().currentNetwork as NetworkType;
    const getInstanceFn = NETWORK_INSTANCE_MAP[currentNetwork] ?? NETWORK_INSTANCE_MAP.solana;
    return getInstanceFn(type);
  }

  static isEvmService(instance: Web3Instance): instance is Web3EvmInteraction {
    return instance instanceof Web3EvmInteraction;
  }

  static create(): Web3TradingService {
    if (!Web3TradingService.instance) {
      Web3TradingService.instance = new Web3TradingService();
    }
    return Web3TradingService.instance;
  }

  destroy(): void {
    Web3TradingService.instance = null;
  }
}
